import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiService } from '../../../services/ApiService';
import type { MonitoredStrategy } from '../../../types/Alert';
import { useDomainContext } from '../../../contexts/DomainContext';
import {
    MONITOR_TEXT,
    MonitorCoreFields,
    MonitorRelevanceRuleFields,
    monitorSecondaryButtonStyle,
    parseLookbackDays
} from './MonitorFormParts';

interface Props {
    strategy: MonitoredStrategy;
    onClose: () => void;
    onSuccess: (updated: MonitoredStrategy) => void;
}

export function EditMonitorModal({ strategy, onClose, onSuccess }: Props) {
    const [name, setName] = useState(strategy.Name || '');
    const [description, setDescription] = useState(strategy.Description || '');
    const [action, setAction] = useState(strategy.Action || '');
    const [lookbackDays, setLookbackDays] = useState<string>(strategy.LookbackDays?.toString() ?? '1');
    const [loading, setLoading] = useState(false);

    const { activeDomainId } = useDomainContext();

    // Relevance Rule fields
    const [relevanceMetric, setRelevanceMetric] = useState<string>(strategy.RelevanceMetric || '');
    const [relevanceOperator, setRelevanceOperator] = useState<string>(strategy.RelevanceOperator || '>');
    const [relevanceThreshold, setRelevanceThreshold] = useState<string>(strategy.RelevanceThreshold?.toString() ?? '');

    const handleSave = async () => {
        setLoading(true);
        try {
            // Parse numbers before sending
            const numLookback = parseLookbackDays(lookbackDays);
            if (numLookback === null) {
                alert(MONITOR_TEXT.invalidLookback);
                return;
            }

            await ApiService.updateAlert(String(strategy.ID), {
                name,
                description,
                action,
                lookback_days: numLookback,
                relevance_metric: relevanceMetric || null,
                relevance_operator: relevanceMetric ? relevanceOperator : null,
                relevance_threshold: relevanceMetric ? parseFloat(relevanceThreshold) : null,
            }, activeDomainId || undefined);

            onSuccess({
                ...strategy,
                Name: name,
                Description: description,
                Action: action,
                LookbackDays: numLookback,
                RelevanceMetric: relevanceMetric || undefined,
                RelevanceOperator: relevanceMetric ? relevanceOperator : undefined,
                RelevanceThreshold: relevanceMetric ? parseFloat(relevanceThreshold) : undefined,
            });
            onClose();
        } catch (e) {
            console.error('Failed to update strategy', e);
            const errorMessage = e instanceof Error ? e.message : MONITOR_TEXT.genericUpdateError;
            alert(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };
    const isValid = name.trim() && description.trim() && action.trim() && parseLookbackDays(lookbackDays) !== null;

    return createPortal(
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'var(--overlay-backdrop)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
        >
            <div
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: 'var(--bg-panel)',
                    color: 'var(--text-primary)',
                    padding: '2rem',
                    borderRadius: '8px',
                    width: '480px',
                    boxShadow: 'var(--shadow-modal)',
                    position: 'relative',
                }}
            >
                <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>✏️ Edit Monitored Strategy</h3>
                <MonitorCoreFields
                    name={name}
                    description={description}
                    action={action}
                    lookbackDays={lookbackDays}
                    onNameChange={setName}
                    onDescriptionChange={setDescription}
                    onActionChange={setAction}
                    onLookbackDaysChange={setLookbackDays}
                />
                <MonitorRelevanceRuleFields
                    relevanceMetric={relevanceMetric}
                    relevanceOperator={relevanceOperator}
                    relevanceThreshold={relevanceThreshold}
                    onRelevanceMetricChange={setRelevanceMetric}
                    onRelevanceOperatorChange={setRelevanceOperator}
                    onRelevanceThresholdChange={setRelevanceThreshold}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={monitorSecondaryButtonStyle}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading || !isValid}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: 'var(--accent-primary)',
                            color: 'white',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: (loading || !isValid) ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        {loading ? 'Saving...' : '💾 Save Changes'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

